import * as THREE from 'three';
import { clamp, damp } from '../engine/mathUtils';
import type { RaycastVehicle } from '../physics/RaycastVehicle';
import { buildProxyBody, buildWheel } from './proxyModel';

/**
 * Visuelle Repraesentation eines Fahrzeugs.
 *
 * Trennt Physik und Darstellung: die Physik laeuft im festen 60-Hz-Takt,
 * gerendert wird interpoliert (`alpha`), damit die Bewegung auch auf
 * 90/120-Hz-Displays glatt bleibt.
 */
export class VehicleView {
  readonly root = new THREE.Group();
  /** Traegt die Karosserie; bekommt die zusaetzliche Kurven-/Bremsneigung. */
  private readonly bodyRoot = new THREE.Group();
  private readonly wheelNodes: THREE.Group[] = [];
  private readonly brakeLightMaterial: THREE.MeshStandardMaterial;
  private readonly connectionPoints: THREE.Vector3[] = [];
  private readonly suspensionDir = new THREE.Vector3(0, -1, 0);

  private readonly prevPos = new THREE.Vector3();
  private readonly prevQuat = new THREE.Quaternion();
  private readonly currPos = new THREE.Vector3();
  private readonly currQuat = new THREE.Quaternion();

  private roll = 0;
  private pitch = 0;
  private lastSpeed = 0;

  constructor(private readonly vehicle: RaycastVehicle, paintColor: number) {
    const spec = vehicle.spec;
    const { body, brakeLights } = buildProxyBody(spec, paintColor);
    this.brakeLightMaterial = brakeLights;
    this.bodyRoot.add(body);
    this.root.add(this.bodyRoot);

    for (const cp of vehicle.wheelConnectionPoints) {
      this.connectionPoints.push(new THREE.Vector3(cp.x, cp.y, cp.z));
      const wheel = buildWheel(spec.wheels.radius, spec.wheels.width);
      this.wheelNodes.push(wheel);
      this.root.add(wheel);
    }

    const t = vehicle.body.translation();
    const r = vehicle.body.rotation();
    this.prevPos.set(t.x, t.y, t.z);
    this.currPos.copy(this.prevPos);
    this.prevQuat.set(r.x, r.y, r.z, r.w);
    this.currQuat.copy(this.prevQuat);
  }

  /**
   * Vor jedem Physik-Substep aufrufen. Liest den Zustand direkt aus der
   * Physik, damit auch bei mehreren Substeps pro Frame genau der Zustand
   * *vor dem letzten* Schritt als Interpolationsanfang dient.
   */
  capturePrevious(): void {
    const t = this.vehicle.body.translation();
    const r = this.vehicle.body.rotation();
    this.prevPos.set(t.x, t.y, t.z);
    this.prevQuat.set(r.x, r.y, r.z, r.w);
  }

  /** Nach dem Physikschritt: aktuellen Zustand uebernehmen. */
  readPhysics(): void {
    const t = this.vehicle.body.translation();
    const r = this.vehicle.body.rotation();
    this.currPos.set(t.x, t.y, t.z);
    this.currQuat.set(r.x, r.y, r.z, r.w);
  }

  /**
   * Einmal pro gerendertem Frame.
   * @param alpha Interpolationsfaktor zwischen letztem und aktuellem Physikschritt.
   */
  update(alpha: number, dt: number, braking: boolean): void {
    this.root.position.lerpVectors(this.prevPos, this.currPos, alpha);
    this.root.quaternion.copy(this.prevQuat).slerp(this.currQuat, alpha);

    const wheels = this.vehicle.wheels;

    for (let i = 0; i < this.wheelNodes.length; i++) {
      const node = this.wheelNodes[i];
      const state = wheels[i];
      const cp = this.connectionPoints[i];
      if (!node || !state || !cp) continue;

      // Federweg sichtbar machen: das Rad haengt an der Raycast-Distanz.
      node.position.copy(cp).addScaledVector(this.suspensionDir, state.suspensionLength);
      node.quaternion.setFromAxisAngle(UP, state.steering);
      node.quaternion.multiply(SPIN.setFromAxisAngle(WHEEL_AXIS, state.rotation));
    }

    // Zusaetzliche, rein optische Neigung. Die Physik neigt die Karosserie
    // ueber die Federung schon, aber deutlich subtiler als es sich fuer
    // ein Kinderspiel gut anfuehlt.
    const speed = this.vehicle.forwardSpeed;
    const accel = dt > 0 ? (speed - this.lastSpeed) / dt : 0;
    this.lastSpeed = speed;

    const lateral = this.lateralLoad();
    this.roll = damp(this.roll, clamp(lateral * 0.035, -0.1, 0.1), 8, dt);
    this.pitch = damp(this.pitch, clamp(-accel * 0.012, -0.06, 0.06), 7, dt);
    this.bodyRoot.rotation.z = this.roll;
    this.bodyRoot.rotation.x = this.pitch;

    const wantsLight = braking || speed < -0.5;
    this.brakeLightMaterial.emissiveIntensity = damp(
      this.brakeLightMaterial.emissiveIntensity,
      wantsLight ? 2.6 : 0.3,
      18,
      dt,
    );
  }

  private lateralLoad(): number {
    const w = this.vehicle.wheels;
    let sum = 0;
    for (const wheel of w) sum += wheel.sideImpulse;
    return sum / Math.max(1, this.vehicle.spec.mass * 0.02);
  }

  dispose(): void {
    this.root.removeFromParent();
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
  }
}

const UP = new THREE.Vector3(0, 1, 0);
/**
 * Raddrehachse. Nachgemessen: `wheelRotation` waechst beim Vorwaertsfahren,
 * und eine positive Drehung um +X laesst den Radvorderpunkt nach unten
 * wandern - genau die Rollrichtung nach vorne (+Z).
 */
const WHEEL_AXIS = new THREE.Vector3(1, 0, 0);
const SPIN = new THREE.Quaternion();
